import { createSlice, PayloadAction } from '@reduxjs/toolkit'

interface DailyBeatState {
  id: string | null
  courseId: string | null
  type: string | null
  label: string | null
  name: string | null
  subName: string | null
  firstItem: any | null
  currentItem: any | null
  imageUrl: string | null
  v2ImageUrl: string | null
  description: string | null
  duration: number | null
  available: boolean | null
  availableDate: string | null
  completed: boolean | null
  percentComplete: number | null
  order: number | null
  locked: boolean | null
  today: boolean | null
  rating: number | null
  contentType: string
  dashboardV2Header: string | null
  infoPageSummary: string | null
  lessonNumber: number | null
  index: number | null
}

export const dailyBeatIsInProgress = (dailyBeat: DailyBeatState) => dailyBeat.currentItem.id !== dailyBeat.firstItem.id

export const selectDailyBeatsByCourseId = (courseId: string) => (state: any) => state.dailyBeats.filter((db: DailyBeatState) => courseId === db.courseId)

export const selectNumDailyBeatsByCourseId = (courseId: string) => (state: any) => state.dailyBeats.filter((db: DailyBeatState) => courseId === db.courseId).length

export const selectDailyBeats = (state: any) => state.dailyBeats

export const selectNumCompleteDailyBeats = (state: any) => state.dailyBeats.filter((db: DailyBeatState) => db.completed).length

export const selectDailyBeatById = (id: string) => (state: any) => state.dailyBeats.find((dailyBeat: DailyBeatState) => id === dailyBeat.id)

const initialState: DailyBeatState[] = [{
  id: null,
  courseId: null,
  type: null,
  label: null,
  name: null,
  subName: null,
  firstItem: null,
  currentItem: null,
  imageUrl: null,
  v2ImageUrl: null,
  description: null,
  duration: null,
  available: null,
  availableDate: null,
  completed: null,
  percentComplete: null,
  order: null,
  locked: null,
  today: null,
  rating: null,
  contentType: 'dailyBeat',
  dashboardV2Header: null,
  infoPageSummary: null,
  lessonNumber: null,
  index: null,
}]

const dailyBeats = createSlice({
  name: 'user',
  initialState,
  reducers: {
    loadDailyBeats: (state, { payload: dailyBeats }: PayloadAction<any[]>) =>
      dailyBeats.map((dailyBeat: any, i: number) => ({
        index: i,
        id: dailyBeat.id,
        courseId: dailyBeat.courseId,
        type: dailyBeat.type,
        label: dailyBeat.label,
        name: dailyBeat.name,
        subName: dailyBeat.subName,
        firstItem: dailyBeat.firstItem,
        currentItem: dailyBeat.firstItem,
        imageUrl: dailyBeat.imageUrl?.trim(),
        v2ImageUrl: dailyBeat.v2ImageUrl?.trim(),
        description: dailyBeat.description,
        duration: dailyBeat.duration,
        available: dailyBeat.available,
        availableDate: dailyBeat.availableDate,
        completed: dailyBeat.completed,
        percentComplete: dailyBeat.percentComplete,
        order: dailyBeat.order,
        locked: dailyBeat.locked,
        today: dailyBeat.today,
        rating: dailyBeat.rating,
        contentType: 'dailyBeat',
        dashboardV2Header: dailyBeat.dashboardV2Header,
        infoPageSummary: dailyBeat.infoPageSummary,
        lessonNumber: dailyBeat.startDayOffset + 1,
      })),
    updateDailyBeatComplete: (state, { payload: id }: PayloadAction<string>) => {
      const dailyBeat = state.find((db) => db.id === id)
      dailyBeat.completed = true
      dailyBeat.percentComplete = 1
    },
    updateDailyBeatRating: (state, { payload: { id, rating } }: PayloadAction<{ id: string; rating: number }>) => {
      const dailyBeat = state.find((db) => db.id === id)
      dailyBeat.rating = rating
    },
    updateDailyBeatCurrentItem: (state, { payload: { id, nextItem } }: PayloadAction<{ id: string; nextItem: any }>) => {
      const dailyBeat = state.find((db) => db.id === id)
      dailyBeat.currentItem = nextItem
    },
    resetDailyBeat: (state, { payload: { id } }: PayloadAction<{ id: string }>) => {
      const dailyBeat = state.find((db) => db.id === id)
      dailyBeat.currentItem = dailyBeat.firstItem
    },
    completeDailyBeat: (state, { payload: { id } }: PayloadAction<{ id: string }>) => {
      const dailyBeat = state.find((db) => db.id === id)
      dailyBeat.completed = true
      dailyBeat.percentComplete = 1
    },
  },
})

export const {
  loadDailyBeats,
  updateDailyBeatComplete,
  updateDailyBeatRating,
  updateDailyBeatCurrentItem,
  completeDailyBeat,
  resetDailyBeat
} = dailyBeats.actions

export default dailyBeats.reducer
